import { createFileRoute, useNavigate, useLocation } from '@tanstack/react-router';
import { useState, useEffect, useRef } from 'react';
import { IconChevronLeft, IconDownload, IconCheck, IconShare2, IconX } from '@tabler/icons-react';
import { capture } from '@tupai/analytics/client';
import { useMinWidth } from '@tupai/shell';
import { useLinksStore } from '@/store/links-store';
import { useChildLink } from '@/lib/use-child-link';
import { isSampleLink } from '@/lib/sample-child';
import { getChildSummaryHistoryReport, markNotificationRead } from '@/lib/api';
import type { ChildSummaryResponse } from '@/lib/api';
import { SummaryReportBody } from '@/components/summary-report-body';
import { PageSpinner, PageError } from '@/components/page-state';
import BottomSheet from '@/components/bottom-sheet';
import ChildHubPage from '@/components/child-hub-page';
import { generateSummaryPdf, shareSummaryPdf, canSharePdf } from '@/lib/pdf';
import { fmtDayMonth, periodLabel } from '@/lib/format';
import { useLanguage } from '@/lib/language';
import { useTranslation } from '@tupai/i18n/react';
import { apiErrorMessage } from '@/lib/api-errors';

export const Route = createFileRoute('/_protected/children/$linkId/reports/$reportId')({
  component: ChildReportViewPage,
});

function childFromLink(link: {
  other_party_name?: string | null;
  other_party_phone: string;
  other_party_detail?: string | null;
}) {
  const parts = (link.other_party_detail ?? '').split(' · ');
  const [syllabus, level] = parts.length >= 2 ? parts : [parts[0], undefined];
  return {
    name: link.other_party_name || link.other_party_phone,
    avatar: (link.other_party_name || '?').charAt(0).toUpperCase(),
    syllabus: syllabus || '',
    level: level || '',
  };
}

// /children/:linkId/reports/:reportId — one archived report, read-only.
// PHUB-020: no Regenerate anywhere, and the PDF export always carries this
// fetched (historic) payload, never the newest live one. The report headline
// lives in the page-head (prototype), not repeated in the body.
function ChildReportViewPage() {
  const { linkId, reportId } = Route.useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { parentLinks } = useLinksStore();
  const locale = useLanguage();
  const { t } = useTranslation('hub');
  const { t: tc } = useTranslation('common');

  const [data, setData] = useState<ChildSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryTick, setRetryTick] = useState(0);
  const [pdfDownloaded, setPdfDownloaded] = useState(false);
  const fetchId = useRef(0);
  // From `lg`, this route is a 672-wide dialog over the child hub
  // (canvas P1-ReportView-1024/1280) rather than its own page — see the
  // `isDesktop` branch in the return below. Behaviour switch only; layout
  // stays CSS everywhere else (DESIGN.md "Tablet and desktop").
  const isDesktop = useMinWidth('lg');

  const link = useChildLink(linkId);
  const linkMissing = !link && parentLinks.length > 0;
  useEffect(() => {
    if (linkMissing) void navigate({ to: '/children', replace: true });
  }, [linkMissing, navigate]);

  useEffect(() => {
    if (!linkId || !reportId) return;
    const id = ++fetchId.current;
    setLoading(true);
    setError(null);
    getChildSummaryHistoryReport(linkId, reportId)
      .then((res) => {
        if (fetchId.current !== id) return;
        setData(res);
        // Fire-and-forget: opening the report is what "read" means. Same
        // marker the bell writes, so the dot clears everywhere (idempotent
        // ON CONFLICT DO NOTHING server-side).
        markNotificationRead('report_ready', reportId).catch(() => {});
      })
      .catch((err) => {
        if (fetchId.current !== id) return;
        setError(apiErrorMessage(err, tc));
      })
      .finally(() => {
        if (fetchId.current !== id) return;
        setLoading(false);
      });
  }, [linkId, reportId, retryTick]);

  // `report_viewed` — is_first_view now prefers the server's `read` flag
  // (Phase 2.7, B3: ChildSummaryResponse.read, same
  // report_ready notification derivation as the history list). This closes
  // the gap the router-state approach below had: a notification deep link
  // or a hard reload carries no router state, and §7.4 says exactly that
  // path is parent return's dominant one — so the event used to under-fire
  // precisely where Q4 needed it most. Router state stays as a fallback
  // ONLY for a server old enough not to send `read` yet; once every deployed
  // server sends it, that branch is dead and can be deleted.
  const reportViewedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (isSampleLink(linkId)) return; // not real usage (lib/sample-child.ts)
    if (!data || !reportId) return;
    if (reportViewedForRef.current === reportId) return;
    let isFirstView: boolean | undefined;
    if (typeof data.read === 'boolean') {
      isFirstView = !data.read;
    } else {
      const navState = location.state;
      if (typeof navState?.isFirstView === 'boolean') isFirstView = navState.isFirstView;
    }
    if (typeof isFirstView !== 'boolean') return;
    reportViewedForRef.current = reportId;
    const ageDays = Math.max(
      0,
      Math.floor((Date.now() - new Date(data.generated_at).getTime()) / 86_400_000)
    );
    capture('report_viewed', {
      report_id: reportId,
      is_first_view: isFirstView,
      age_days: ageDays,
    });
  }, [data, reportId, linkId, location.state]);

  if (!link) {
    return parentLinks.length === 0 ? <PageSpinner /> : null;
  }

  const child = childFromLink(link);

  const pdfParams = data
    ? {
        childName: child.name,
        syllabus: child.syllabus || 'KSSM',
        grade: child.level,
        generatedAt: data.generated_at,
        periodDays: data.period_days,
        overview: data.overview,
        strengths: data.strengths,
        improvements: data.improvements,
        recommendations: data.recommendations,
        appUsage: data.appUsage,
        locale,
      }
    : null;

  const title = data?.title ?? t('reportFallbackTitle', { childName: child.name });

  const downloadPdf = () => {
    if (!pdfParams) return;
    generateSummaryPdf(pdfParams);
    setPdfDownloaded(true);
    setTimeout(() => setPdfDownloaded(false), 2000);
  };

  // Dialog branch replaces so Back doesn't reopen the dialog (the hub
  // underneath is already the right history entry); the phone header is a
  // real navigation and keeps a push so Back still lands here from deeper.
  const backToHubFromDialog = () =>
    navigate({ to: '/children/$linkId', params: { linkId }, replace: true });
  const backToHub = () => navigate({ to: '/children/$linkId', params: { linkId } });

  if (isDesktop) {
    // Report is a dialog over the child hub from `lg` (canvas
    // P1-ReportView-1024/1280) — the hub renders as the backdrop, the
    // report lives in a `bare` sheet that owns its own header (single
    // Download PDF action; Share is dropped per the canvas) and body.
    return (
      <>
        <ChildHubPage backdrop />
        <BottomSheet open wide bare onClose={backToHubFromDialog} ariaLabel={title}>
          <div className="flex h-14 items-center gap-2 border-b border-border px-4">
            <p className="flex-1 truncate text-base font-semibold">{title}</p>
            {data && (
              <button
                onClick={downloadPdf}
                className="min-h-11 text-sm font-bold text-primary cursor-pointer"
              >
                {pdfDownloaded ? t('downloaded') : t('downloadPdf')}
              </button>
            )}
            <button
              aria-label={tc('close')}
              onClick={backToHubFromDialog}
              className="flex size-11 items-center justify-center rounded-pill text-muted hover:bg-bg cursor-pointer"
            >
              <IconX size={20} stroke={2} />
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-5 py-5 flex flex-col gap-5">
            {loading ? (
              <PageSpinner />
            ) : error || !data ? (
              <PageError message={error ?? undefined} onRetry={() => setRetryTick((t) => t + 1)} />
            ) : (
              <SummaryReportBody data={data} assignable={false} archived />
            )}
          </div>
        </BottomSheet>
      </>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-surface">
      {/* Prototype page-head: back + left-aligned report headline with the
          date · period sub line. Rendered in every state so back to the hub
          is always one tap, even while loading or on error. */}
      <div className="bg-surface border-b border-border-light pl-1 pr-4 py-2 flex-shrink-0 safe-area-top flex items-center gap-1">
        <button
          aria-label={t('back')}
          onClick={backToHub}
          className="w-11 h-11 rounded-pill flex items-center justify-center text-content transition-all hover:bg-bg cursor-pointer shrink-0"
        >
          <IconChevronLeft size={20} stroke={2.5} />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-lg font-bold text-content truncate">{title}</p>
          {data && (
            <p className="text-sm text-muted font-semibold truncate">
              {fmtDayMonth(data.generated_at, locale)} ·{' '}
              {periodLabel(data.period_start, data.period_end, locale)}
            </p>
          )}
        </div>
      </div>

      {loading ? (
        <PageSpinner />
      ) : error || !data ? (
        <PageError message={error ?? undefined} onRetry={() => setRetryTick((t) => t + 1)} />
      ) : (
        <div className="flex-1 overflow-y-auto px-5 py-5 flex flex-col gap-5">
          <SummaryReportBody data={data} assignable={false} archived />

          {/* No PDF of the sample child's made-up report. */}
          {!isSampleLink(linkId) && (
            <div className="flex gap-3 pb-2">
              <button
                onClick={downloadPdf}
                className="flex-1 h-11 bg-surface border border-border hover:border-primary text-sm font-bold text-content hover:text-primary rounded-md transition-all cursor-pointer active:scale-98 flex items-center justify-center gap-1.5"
              >
                {pdfDownloaded ? (
                  <>
                    <IconCheck size={14} stroke={2.5} /> {t('downloaded')}
                  </>
                ) : (
                  <>
                    <IconDownload size={14} stroke={2.5} /> {t('downloadPdf')}
                  </>
                )}
              </button>
              {canSharePdf && (
                <button
                  onClick={() => pdfParams && shareSummaryPdf(pdfParams)}
                  className="flex-1 h-11 bg-surface border border-border hover:border-primary text-sm font-bold text-content hover:text-primary rounded-md transition-all cursor-pointer active:scale-98 flex items-center justify-center gap-1.5"
                >
                  <IconShare2 size={14} stroke={2.5} /> {t('share')}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
